import React, { useContext, useState } from 'react';
import { ConfigurationContext } from '../DataContexts/ConfigurationContext';
import ConfigSection from './ConfigSection';
import ModelCard from './ModelCard';
import { formatDuration, parseIntOrNull, simulateLongTrackCredits } from './configFormat';

const emptyRow = () => ({ seconds: '', credits: '' });

const parseRow = (row) => ({
  seconds: parseIntOrNull(row.seconds),
  credits: parseIntOrNull(row.credits),
});

const isValidEntry = (entry) =>
  entry.seconds != null && entry.seconds > 0 && entry.credits != null && entry.credits >= 0;

// DJB-164: overview of all long-track price models (seconds threshold →
// credits) plus create-new + delete. No edit of existing models — the backend
// has no PUT for these.
const LongTrackSection = () => {
  const { longTrackModes, longTrackModesLoading, createLongTrackMode, deleteLongTrackMode } =
    useContext(ConfigurationContext);
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState('');
  const [rows, setRows] = useState([emptyRow()]);
  const [formError, setFormError] = useState(null);
  const [saving, setSaving] = useState(false);

  const resetForm = () => {
    setName('');
    setRows([emptyRow()]);
    setFormError(null);
  };

  const updateRow = (index, field, value) => {
    setRows((prev) => prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)));
  };

  // DJB-201: repeat duplicates the last entry's span and credits — the new
  // threshold sits the same number of seconds after the last one as the last
  // sits after the one before it (200 → 400 → 600 …).
  const repeatPreviousEntry = () => {
    setRows((prev) => {
      const parsed = prev.map(parseRow);
      const last = parsed[parsed.length - 1];
      if (!last || !isValidEntry(last)) return prev;
      const beforeLast = parsed.length > 1 ? parsed[parsed.length - 2] : null;
      const step =
        beforeLast && isValidEntry(beforeLast) && beforeLast.seconds < last.seconds
          ? last.seconds - beforeLast.seconds
          : last.seconds;
      return [...prev, { seconds: String(last.seconds + step), credits: String(last.credits) }];
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!name.trim()) {
      setFormError('Give the price model a name.');
      return;
    }
    const entries = [];
    for (const row of rows) {
      if (row.seconds === '' && row.credits === '') continue;
      const seconds = parseIntOrNull(row.seconds);
      const credits = parseIntOrNull(row.credits);
      if (seconds == null || seconds <= 0 || credits == null || credits < 0) {
        setFormError('Every entry needs a duration in seconds and a credit price.');
        return;
      }
      entries.push({ seconds, credits });
    }
    if (entries.length === 0) {
      setFormError('Add at least one duration → credits entry.');
      return;
    }
    entries.sort((a, b) => a.seconds - b.seconds);
    setFormError(null);
    setSaving(true);
    try {
      await createLongTrackMode({ name: name.trim(), entries });
      resetForm();
      setShowForm(false);
    } catch (error) {
      // notification shown by the context; keep the form open for a retry
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (mode) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete long-track price model "${mode.name}"?`
    );
    if (!confirmed) return;
    try {
      await deleteLongTrackMode(mode.id);
    } catch (error) {
      // notification shown by the context
    }
  };

  const sortedEntries = (mode) =>
    [...(mode.entries || [])].sort((a, b) => (a.seconds ?? 0) - (b.seconds ?? 0));

  // DJB-201: live simulated total cost per form row (1 base credit + every
  // threshold at or below that row's duration, counting only valid rows).
  const parsedRows = rows.map(parseRow);
  const validFormEntries = parsedRows.filter(isValidEntry);
  const simulatedTotal = (index) =>
    isValidEntry(parsedRows[index])
      ? simulateLongTrackCredits(validFormEntries, parsedRows[index].seconds)
      : null;
  const lastParsedRow = parsedRows[parsedRows.length - 1];
  const canRepeat = Boolean(lastParsedRow && isValidEntry(lastParsedRow));

  return (
    <ConfigSection
      anchorId="section-longtrack-length"
      title="LongTrack Length"
      actions={
        !showForm && (
          <button type="button" className="config-primary-btn" onClick={() => setShowForm(true)}>
            New price model
          </button>
        )
      }
    >
      <p className="config-section-intro">
        Credit price for long tracks by duration threshold: a track costs 1 base credit plus the
        extra credits of every threshold it passes. A model is assigned to a machine; its entries
        are fixed at creation (create a new model to change them).
      </p>

      {showForm && (
        <form className="config-form" onSubmit={handleSubmit}>
          <div className="config-form-field">
            <label htmlFor="longtrack-mode-name">Name</label>
            <input
              id="longtrack-mode-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. 400-2 / 600-3"
            />
          </div>
          <div className="config-entry-editor">
            <div className="config-entry-editor-head">
              <span className="config-entry-editor-label">
                Entries (from duration → extra credits)
              </span>
              <span className="config-entry-editor-hint">
                total cost = 1 base credit + every passed threshold
              </span>
            </div>
            {rows.map((row, index) => (
              <div className="config-entry-row" key={index}>
                <input
                  type="number"
                  min="1"
                  step="1"
                  placeholder="Seconds"
                  aria-label={`Entry ${index + 1} duration in seconds`}
                  value={row.seconds}
                  onChange={(e) => updateRow(index, 'seconds', e.target.value)}
                />
                <span className="config-entry-arrow" aria-hidden="true">→</span>
                <input
                  type="number"
                  min="0"
                  step="1"
                  placeholder="Credits"
                  aria-label={`Entry ${index + 1} credits`}
                  value={row.credits}
                  onChange={(e) => updateRow(index, 'credits', e.target.value)}
                />
                <span
                  className="config-entry-total"
                  aria-label={`Entry ${index + 1} simulated total credits`}
                >
                  {simulatedTotal(index) == null ? '—' : `= ${simulatedTotal(index)} cr`}
                </span>
                <button
                  type="button"
                  className="config-icon-btn"
                  aria-label={`Remove entry ${index + 1}`}
                  onClick={() => setRows((prev) => prev.filter((_, i) => i !== index))}
                >
                  ×
                </button>
              </div>
            ))}
            <div className="config-entry-editor-actions">
              <button
                type="button"
                className="config-ghost-btn"
                onClick={() => setRows((prev) => [...prev, emptyRow()])}
              >
                Add entry
              </button>
              <button
                type="button"
                className="config-ghost-btn"
                onClick={repeatPreviousEntry}
                disabled={!canRepeat}
                title="Add an entry one span further: same credits, previous duration plus the previous span"
              >
                Repeat previous entry
              </button>
            </div>
          </div>
          {formError && (
            <p className="config-form-error" role="alert">
              {formError}
            </p>
          )}
          <div className="config-form-actions">
            <button
              type="button"
              className="config-ghost-btn"
              onClick={() => {
                resetForm();
                setShowForm(false);
              }}
            >
              Cancel
            </button>
            <button type="submit" className="config-primary-btn" disabled={saving}>
              {saving ? 'Creating…' : 'Create price model'}
            </button>
          </div>
        </form>
      )}

      {longTrackModesLoading ? (
        <p className="config-muted">Loading price models…</p>
      ) : longTrackModes.length === 0 ? (
        <p className="config-muted">No long-track price models yet.</p>
      ) : (
        <div className="config-mode-cards">
          {longTrackModes.map((mode) => (
            <ModelCard
              key={mode.id}
              title={mode.name}
              actions={
                <button
                  type="button"
                  className="config-danger-btn"
                  onClick={() => handleDelete(mode)}
                  aria-label={`Delete long-track price model ${mode.name}`}
                >
                  Delete
                </button>
              }
            >
              {sortedEntries(mode).length === 0 ? (
                <p className="config-muted">No entries</p>
              ) : (
                <table className="config-tier-table">
                  <thead>
                    <tr>
                      <th scope="col">From duration</th>
                      <th scope="col">Extra credits</th>
                      <th scope="col">Total credits</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedEntries(mode).map((entry, index) => (
                      <tr key={index}>
                        <td>
                          {formatDuration(entry.seconds)} ({entry.seconds}s)
                        </td>
                        <td>{entry.credits}</td>
                        <td>{simulateLongTrackCredits(mode.entries, entry.seconds)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </ModelCard>
          ))}
        </div>
      )}
    </ConfigSection>
  );
};

export default LongTrackSection;
